import { FaFlask, FaXRay, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { MdScience, MdLocalHospital } from "react-icons/md";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";
import ProgressBar from "react-bootstrap/ProgressBar";

type ExamTypeStats = {
	requested: number;
	completed: number;
	cancelled: number;
};

type Props = {
	totalLaboratoryExams: ExamTypeStats;
	totalRadiographyExams: ExamTypeStats;
};

export function ExamTypeStatistics({ totalLaboratoryExams, totalRadiographyExams }: Props) {
	// Cálculos para Laboratório
	const labCompletionRate =
		totalLaboratoryExams.requested > 0
			? ((totalLaboratoryExams.completed / totalLaboratoryExams.requested) * 100).toFixed(1)
			: "0";
	const labCancellationRate =
		totalLaboratoryExams.requested > 0
			? ((totalLaboratoryExams.cancelled / totalLaboratoryExams.requested) * 100).toFixed(1)
			: "0";

	// Cálculos para Radiografia
	const radCompletionRate =
		totalRadiographyExams.requested > 0
			? ((totalRadiographyExams.completed / totalRadiographyExams.requested) * 100).toFixed(1)
			: "0";
	const radCancellationRate =
		totalRadiographyExams.requested > 0
			? ((totalRadiographyExams.cancelled / totalRadiographyExams.requested) * 100).toFixed(1)
			: "0";

	return (
		<Row className="p-3">
			{/* Card - Exames Laboratoriais */}
			<Col sm={12} lg={6} className="mb-3">
				<Card className="h-100 shadow-sm border-0">
					<Card.Header className="bg-gradient-primary text-white border-0">
						<div className="d-flex align-items-center justify-content-center">
							<div className="bg-white bg-opacity-20 rounded-circle p-2 me-3">
								<MdScience size={24} />
							</div>
							<div>
								<h5 className="mb-1 fw-bold">Exames Laboratoriais</h5>
								<small className="opacity-75">Análises Clínicas</small>
							</div>
						</div>
					</Card.Header>

					<Card.Body className="p-4">
						{/* Métricas Principais */}
						<Row className="mb-4">
							<Col sm={4} className="text-center">
								<div className="p-3 bg-primary bg-opacity-10 rounded">
									<FaFlask className="text-primary mb-2" size={20} />
									<h4 className="mb-1 fw-bold text-primary">
										{totalLaboratoryExams.requested.toLocaleString()}
									</h4>
									<small className="text-muted">Solicitados</small>
								</div>
							</Col>
							<Col sm={4} className="text-center">
								<div className="p-3 bg-success bg-opacity-10 rounded">
									<FaCheckCircle className="text-success mb-2" size={20} />
									<h4 className="mb-1 fw-bold text-success">
										{totalLaboratoryExams.completed.toLocaleString()}
									</h4>
									<small className="text-muted">Concluídos</small>
								</div>
							</Col>
							<Col sm={4} className="text-center">
								<div className="p-3 bg-danger bg-opacity-10 rounded">
									<FaTimesCircle className="text-danger mb-2" size={20} />
									<h4 className="mb-1 fw-bold text-danger">
										{totalLaboratoryExams.cancelled.toLocaleString()}
									</h4>
									<small className="text-muted">Cancelados</small>
								</div>
							</Col>
						</Row>

						{/* Barras de Progresso */}
						<div className="space-y-3">
							<div>
								<div className="d-flex justify-content-between mb-1">
									<small className="text-muted">Taxa de Conclusão</small>
									<small className="text-success fw-bold">
										{labCompletionRate}%
									</small>
								</div>
								<ProgressBar
									variant="success"
									now={parseFloat(labCompletionRate)}
									className="mb-3"
									style={{ height: "8px" }}
								/>
							</div>

							<div>
								<div className="d-flex justify-content-between mb-1">
									<small className="text-muted">Taxa de Cancelamento</small>
									<small className="text-danger fw-bold">
										{labCancellationRate}%
									</small>
								</div>
								<ProgressBar
									variant="danger"
									now={parseFloat(labCancellationRate)}
									style={{ height: "8px" }}
								/>
							</div>
						</div>

						{/* Estatísticas Adicionais */}
						<div className="mt-4 p-3 bg-light rounded">
							<div className="row text-center">
								<Col sm={6}>
									<small className="text-muted d-block">Eficiência</small>
									<span className="fw-bold text-success">
										{labCompletionRate}%
									</span>
								</Col>
								<Col sm={6}>
									<small className="text-muted d-block">Pendentes</small>
									<span className="fw-bold text-warning">
										{(
											totalLaboratoryExams.requested -
											totalLaboratoryExams.completed -
											totalLaboratoryExams.cancelled
										).toLocaleString()}
									</span>
								</Col>
							</div>
						</div>
					</Card.Body>
				</Card>
			</Col>

			{/* Card - Exames de Radiografia */}
			<Col sm={12} lg={6} className="mb-3">
				<Card className="h-100 shadow-sm border-0">
					<Card.Header className="bg-gradient-info text-white border-0">
						<div className="d-flex align-items-center justify-content-center">
							<div className="bg-white bg-opacity-20 rounded-circle p-2 me-3">
								<MdLocalHospital size={24} />
							</div>
							<div>
								<h5 className="mb-1 fw-bold">Exames de Radiografia</h5>
								<small className="opacity-75">Imagens Diagnósticas</small>
							</div>
						</div>
					</Card.Header>

					<Card.Body className="p-4">
						{/* Métricas Principais */}
						<Row className="mb-4">
							<Col sm={4} className="text-center">
								<div className="p-3 bg-primary bg-opacity-10 rounded">
									<FaXRay className="text-primary mb-2" size={20} />
									<h4 className="mb-1 fw-bold text-primary">
										{totalRadiographyExams.requested.toLocaleString()}
									</h4>
									<small className="text-muted">Solicitados</small>
								</div>
							</Col>
							<Col sm={4} className="text-center">
								<div className="p-3 bg-success bg-opacity-10 rounded">
									<FaCheckCircle className="text-success mb-2" size={20} />
									<h4 className="mb-1 fw-bold text-success">
										{totalRadiographyExams.completed.toLocaleString()}
									</h4>
									<small className="text-muted">Concluídos</small>
								</div>
							</Col>
							<Col sm={4} className="text-center">
								<div className="p-3 bg-danger bg-opacity-10 rounded">
									<FaTimesCircle className="text-danger mb-2" size={20} />
									<h4 className="mb-1 fw-bold text-danger">
										{totalRadiographyExams.cancelled.toLocaleString()}
									</h4>
									<small className="text-muted">Cancelados</small>
								</div>
							</Col>
						</Row>

						{/* Barras de Progresso */}
						<div className="space-y-3">
							<div>
								<div className="d-flex justify-content-between mb-1">
									<small className="text-muted">Taxa de Conclusão</small>
									<small className="text-success fw-bold">
										{radCompletionRate}%
									</small>
								</div>
								<ProgressBar
									variant="success"
									now={parseFloat(radCompletionRate)}
									className="mb-3"
									style={{ height: "8px" }}
								/>
							</div>

							<div>
								<div className="d-flex justify-content-between mb-1">
									<small className="text-muted">Taxa de Cancelamento</small>
									<small className="text-danger fw-bold">
										{radCancellationRate}%
									</small>
								</div>
								<ProgressBar
									variant="danger"
									now={parseFloat(radCancellationRate)}
									style={{ height: "8px" }}
								/>
							</div>
						</div>

						{/* Estatísticas Adicionais */}
						<div className="mt-4 p-3 bg-light rounded">
							<div className="row text-center">
								<Col sm={6}>
									<small className="text-muted d-block">Eficiência</small>
									<span className="fw-bold text-success">
										{radCompletionRate}%
									</span>
								</Col>
								<Col sm={6}>
									<small className="text-muted d-block">Pendentes</small>
									<span className="fw-bold text-warning">
										{(
											totalRadiographyExams.requested -
											totalRadiographyExams.completed -
											totalRadiographyExams.cancelled
										).toLocaleString()}
									</span>
								</Col>
							</div>
						</div>
					</Card.Body>
				</Card>
			</Col>
		</Row>
	);
}
